import { shortDate } from "@/lib/format"
import { SERIES_SLOTS, seriesColor } from "@/lib/seriesColor"
import type { SalesChannel, SalesDay, UnitsOut } from "../types"
import type { ActionMark, NumberedAction } from "./actions"

// Sales & Gifts graph points from the daily figures the server sends (today
// last), the Push review graph's periods: Day shows the last 90 days, Week
// the last 26 weeks ending today, Month the last 6 calendar months with this
// one "so far".

export type Grain = "day" | "week" | "month"

export interface SalesPoint {
  i: number // 0 = oldest
  tick: string // label on the date axis
  title: string // heading in the tooltip
  sold: number
  gifted: number
  channels: Record<string, UnitsOut> // by channel id
}

// One bar in the Movement By Channel graph: a channel, or "Other" for the
// channels past the last color slot.
export interface ChannelSeries {
  key: string
  name: string
  color: string
  ids: string[]
}

const MONTH = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" })
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" })

function buckets(days: SalesDay[], grain: Grain, today: string): { tick: string; title: string; days: SalesDay[] }[] {
  if (grain === "day") {
    return days.slice(-90).map((d) => ({ tick: shortDate(d.date, today), title: shortDate(d.date, today), days: [d] }))
  }

  if (grain === "week") {
    const count = Math.min(26, Math.floor(days.length / 7))
    const recent = days.slice(-count * 7)
    return Array.from({ length: count }, (_, i) => {
      const week = recent.slice(i * 7, i * 7 + 7)
      const first = shortDate(week[0].date, today)
      return { tick: first, title: `${first} – ${shortDate(week[week.length - 1].date, today)}`, days: week }
    })
  }

  const months = new Map<string, SalesDay[]>()
  for (const d of days) months.set(d.date.slice(0, 7), [...(months.get(d.date.slice(0, 7)) ?? []), d])
  return [...months.entries()].slice(-6).map(([key, list]) => {
    const date = new Date(`${key}-01T00:00:00Z`)
    const soFar = key === today.slice(0, 7) ? " (so far)" : ""
    return { tick: MONTH.format(date) + soFar, title: MONTH_YEAR.format(date) + soFar, days: list }
  })
}

export function salesSeries(days: SalesDay[], grain: Grain, today: string): SalesPoint[] {
  return buckets(days, grain, today).map((b, i) => ({
    i,
    tick: b.tick,
    title: b.title,
    sold: b.days.reduce((sum, d) => sum + d.sold, 0),
    gifted: b.days.reduce((sum, d) => sum + d.gifted, 0),
    channels: sumChannels(b.days),
  }))
}

function sumChannels(days: SalesDay[]): Record<string, UnitsOut> {
  const out: Record<string, UnitsOut> = {}
  for (const d of days) {
    for (const [id, c] of Object.entries(d.channels)) {
      const sum = (out[id] ??= { sold: 0, gifted: 0 })
      sum.sold += c.sold
      sum.gifted += c.gifted
    }
  }
  return out
}

// Colors follow the channel's place in the market (org-tree order), never
// which channels sold, so a channel keeps its color from product to product.
export function channelSeries(channels: SalesChannel[]): ChannelSeries[] {
  const named = channels.slice(0, SERIES_SLOTS).map((c, i) => ({
    key: c.id, name: c.name, color: seriesColor(i), ids: [c.id],
  }))
  const rest = channels.slice(SERIES_SLOTS)
  if (rest.length === 0) return named
  return [...named, { key: "other", name: "Other", color: seriesColor(SERIES_SLOTS), ids: rest.map((c) => c.id) }]
}

// One bar's units in one point.
export function seriesUnits(point: SalesPoint, series: ChannelSeries): UnitsOut {
  return series.ids.reduce(
    (sum, id) => ({ sold: sum.sold + (point.channels[id]?.sold ?? 0), gifted: sum.gifted + (point.channels[id]?.gifted ?? 0) }),
    { sold: 0, gifted: 0 },
  )
}

// Marks for the Sales & Gifts graph: each point with the actions dated
// inside its day, week or month.
export function salesMarks(days: SalesDay[], grain: Grain, today: string, actions: NumberedAction[]): ActionMark[] {
  return buckets(days, grain, today).flatMap((b, x) => {
    const inside = actions.filter((a) => b.days.some((d) => d.date === a.date))
    return inside.length > 0 ? [{ x, actions: inside }] : []
  })
}
